import { ArrowLeftRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DetailCard } from '@/admin/components/shared/detail-card'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useTransfers } from '@/admin/hooks/use-transfers'
import { useFormatters } from '@/shared/lib/formatting'

/**
 * Transfers covering the claim's registration, so a reviewer sees an owner
 * change in progress. Renders only for users who can view transfers, and only
 * once the registration has one (``useTransfers`` is off without the flag).
 */
export function ClaimTransfersCard({ holdingUuid }: { holdingUuid: string }) {
  const { can } = usePermissions()
  if (!can('transfers.view')) return null
  return <ClaimTransfersList holdingUuid={holdingUuid} />
}

function ClaimTransfersList({ holdingUuid }: { holdingUuid: string }) {
  const { t } = useTranslation()
  const { formatDate } = useFormatters()
  const { data } = useTransfers({ holding: holdingUuid })
  const transfers = data?.results ?? []
  if (transfers.length === 0) return null

  return (
    <DetailCard
      title={t('claims.card.transfers', 'Transfers')}
      icon={<ArrowLeftRight className="h-4 w-4" />}
      collapsible={false}
    >
      <div className="p-4">
        <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
          {transfers.map((transfer, i) => (
            <div
              key={transfer.uuid}
              className={`flex items-center justify-between gap-3 px-4 py-2.5 ${
                i === transfers.length - 1 ? '' : 'border-b-[0.5px] border-b-[var(--rule)]'
              }`}
              data-testid="claim-transfer-row"
            >
              <a
                href={`/transfers/${transfer.uuid}`}
                className="text-[12px] font-medium text-[var(--ink)] underline decoration-[var(--ink-3)] underline-offset-[3px] hover:decoration-[var(--ink)]"
              >
                {`${transfer.new_owner_first_name} ${transfer.new_owner_last_name}`.trim() ||
                  t('claims.card.transferFallback', 'Transfer')}
              </a>
              <span className="text-[12px] text-[var(--ink-2)]">
                {[
                  transfer.transfer_type?.label,
                  transfer.status_label ?? transfer.status,
                  formatDate(transfer.created_at),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          ))}
        </div>
      </div>
    </DetailCard>
  )
}
